import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Tarjeta } from '../componentes/ui/basicos';
import { Dialogo } from '../componentes/ui/dialogo';
import { Campo, Selector } from '../componentes/ui/formulario';
import { Cargando, Problema, Vacio, useTextoDeError } from '../componentes/ui/estados';
import { Tabla, type Columna } from '../componentes/ui/tabla';
import { usePermiso } from '../sesion/ProveedorDeSesion';
import { importeParaMirar } from '../lib/numero';
import { CostoYMargen } from '../componentes/CostoYMargen';

interface Deposito {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
}

interface FilaDeExistencia {
  readonly depositoId: string;
  readonly deposito: string;
  readonly varianteId: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly talle: string | null;
  readonly color: string | null;
  readonly esUnica: boolean;
  readonly unidad: string;
  readonly decimales: number;
  readonly cantidad: string;
  readonly valorizacion?: string;
}

interface Movimiento {
  readonly id: string;
  readonly cantidad: string;
  readonly origen: string;
  readonly motivo: string | null;
  readonly costoUnitario: string | null;
  readonly ocurridoEn: string;
  readonly correoDelUsuario: string | null;
  readonly observacion: string | null;
}

interface VarianteEncontrada {
  readonly varianteId: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly decimales: number;
}

const MOTIVOS = [
  'existencia_inicial',
  'conteo',
  'rotura',
  'faltante',
  'sobrante',
  'uso_interno',
  'devolucion_a_proveedor',
] as const;

interface LineaDelAjuste {
  readonly clave: string;
  varianteId: string;
  etiqueta: string;
  cantidad: string;
  costoUnitario: string;
  vencimiento: string;
}

/** Cómo se nombra una variante en la búsqueda: "TORNILLO — Tornillo autoperforante". */
function etiquetaDeVariante(variante: VarianteEncontrada): string {
  return `${variante.codigo} — ${variante.nombre}`;
}

/** `existencia_inicial` → `existencias.motivoExistenciaInicial`. */
export function claveTraducida(prefijo: string, valor: string): string {
  const camello = valor.replace(/_(.)/g, (_, letra: string) => letra.toUpperCase());
  return `existencias.${prefijo}${camello.charAt(0).toUpperCase()}${camello.slice(1)}`;
}

/** La cantidad se muestra con los decimales de su unidad y no con los tres de la escala. */
function conDecimales(cantidad: string, decimales: number): string {
  const [entera, fraccion = ''] = cantidad.split('.');
  if (decimales === 0) return entera ?? '0';
  return `${entera ?? '0'}.${fraccion.padEnd(decimales, '0').slice(0, decimales)}`;
}

/**
 * Existencias: ver el saldo y ajustarlo.
 *
 * Dos cosas que se ven al usarla y que no son adorno.
 *
 * **El motivo del ajuste se elige primero y no tiene valor por defecto.** Es la diferencia
 * entre un inventario que se puede auditar y una tabla de números que alguien cambió alguna
 * vez. Poner un motivo por defecto sería elegir la explicación por el usuario.
 *
 * **El ajuste se carga por líneas, en una sola operación.** Un conteo de depósito produce
 * treinta diferencias juntas, no treinta visitas a un formulario; y las treinta se aplican
 * enteras o no se aplica ninguna.
 */
export function Existencias(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const clienteDeConsultas = useQueryClient();
  const textoDeError = useTextoDeError();

  const puedeAjustar = usePermiso('EXISTENCIAS.AJUSTE.APLICAR');
  const puedeVerKardex = usePermiso('EXISTENCIAS.KARDEX.VER');
  const puedeVerCosto = usePermiso('EXISTENCIAS.COSTO.VER');

  const [depositoId, setDepositoId] = useState('');
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState<(typeof MOTIVOS)[number]>('existencia_inicial');
  const [lineas, setLineas] = useState<LineaDelAjuste[]>([]);
  const [busquedaDeArticulo, setBusquedaDeArticulo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kardexDe, setKardexDe] = useState<FilaDeExistencia | null>(null);
  const [costoDe, setCostoDe] = useState<FilaDeExistencia | null>(null);

  const depositos = useQuery({
    queryKey: ['depositos'],
    queryFn: () => pedir<Deposito[]>('/depositos'),
  });

  const consulta = useQuery({
    queryKey: ['existencias', depositoId],
    queryFn: () =>
      pedir<FilaDeExistencia[]>(
        depositoId === '' ? '/existencias' : `/existencias?depositoId=${depositoId}`,
      ),
  });

  const candidatos = useQuery({
    queryKey: ['articulos-para-ajustar', busquedaDeArticulo],
    enabled: abierto && busquedaDeArticulo.trim() !== '',
    queryFn: () =>
      pedir<VarianteEncontrada[]>(
        `/articulos/buscar?texto=${encodeURIComponent(busquedaDeArticulo.trim())}`,
      ),
  });

  const kardex = useQuery({
    queryKey: ['kardex', kardexDe?.depositoId, kardexDe?.varianteId],
    enabled: kardexDe !== null,
    queryFn: () =>
      pedir<Movimiento[]>(
        `/existencias/${kardexDe?.depositoId ?? ''}/${kardexDe?.varianteId ?? ''}/kardex`,
      ),
  });

  const depositoDelAjuste = depositoId === '' ? (depositos.data?.[0]?.id ?? '') : depositoId;

  const aplicar = useMutation({
    mutationFn: () =>
      pedir<{ movimientos: number }>('/existencias/ajustes', {
        method: 'POST',
        cuerpo: {
          depositoId: depositoDelAjuste,
          motivo,
          lineas: lineas.map((linea) => ({
            varianteId: linea.varianteId,
            cantidad: linea.cantidad.trim().replace(',', '.'),
            ...(linea.costoUnitario.trim() === ''
              ? {}
              : { costoUnitario: linea.costoUnitario.trim().replace(',', '.') }),
            ...(linea.vencimiento === '' ? {} : { vencimiento: linea.vencimiento }),
          })),
        },
      }),
    onSuccess: async (resultado) => {
      setAbierto(false);
      setLineas([]);
      setError(null);
      setAviso(t('existencias.aplicado', { count: resultado.movimientos }));
      await clienteDeConsultas.invalidateQueries({ queryKey: ['existencias'] });
    },
    onError: (fallo: unknown) => {
      setError(textoDeError(fallo));
    },
  });

  const columnas: Columna<FilaDeExistencia>[] = [
    {
      clave: 'codigo',
      titulo: t('existencias.codigo'),
      contenido: (fila) => <span className="numero">{fila.codigo}</span>,
    },
    {
      clave: 'nombre',
      titulo: t('existencias.articulo'),
      contenido: (fila) => (
        <>
          {fila.nombre}
          {fila.esUnica ? null : (
            <span className="block text-xs text-texto-tenue">
              {[fila.talle, fila.color].filter((parte) => parte !== null).join(' · ')}
            </span>
          )}
        </>
      ),
    },
    { clave: 'deposito', titulo: t('existencias.deposito'), contenido: (fila) => fila.deposito },
    { clave: 'unidad', titulo: t('existencias.unidad'), contenido: (fila) => fila.unidad },
    {
      clave: 'cantidad',
      titulo: t('existencias.cantidad'),
      numerica: true,
      contenido: (fila) => (
        // En rojo cuando es negativa: puede pasar con una venta de política abierta, y es
        // una anomalía que tiene que saltar a la vista, no un número más.
        <span className={`numero ${fila.cantidad.startsWith('-') ? 'text-mal' : ''}`}>
          {conDecimales(fila.cantidad, fila.decimales)}
        </span>
      ),
    },
    ...(puedeVerCosto
      ? [
          {
            clave: 'valorizacion',
            titulo: t('existencias.valorizacion'),
            numerica: true,
            // Los cuatro decimales son precisión interna y en pantalla estorban: `200925.0000`
            // se lee peor que `200.925,00` y no dice nada más.
            contenido: (fila: FilaDeExistencia) => (
              <span className="numero">
                {fila.valorizacion == null
                  ? '—'
                  : importeParaMirar(fila.valorizacion, i18n.language)}
              </span>
            ),
          },
        ]
      : []),
    ...(puedeVerCosto
      ? [
          {
            clave: 'costo',
            // Sin encabezado: una columna de acciones no tiene nombre, tiene botones.
            titulo: '',
            contenido: (fila: FilaDeExistencia) => (
              <Boton
                tono="sutil"
                onClick={() => {
                  setCostoDe(fila);
                }}
              >
                {t('compras.costoYMargen')}
              </Boton>
            ),
          },
        ]
      : []),
    ...(puedeVerKardex
      ? [
          {
            clave: 'kardex',
            titulo: t('existencias.kardex'),
            contenido: (fila: FilaDeExistencia) => (
              <Boton
                tono="sutil"
                onClick={() => {
                  setKardexDe(fila);
                }}
              >
                {t('existencias.kardex')}
              </Boton>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Encabezado
        titulo={t('existencias.titulo')}
        descripcion={t('existencias.descripcion')}
        acciones={
          puedeAjustar ? (
            <Boton
              tono="principal"
              onClick={() => {
                setAbierto(true);
                setError(null);
              }}
            >
              {t('existencias.ajustar')}
            </Boton>
          ) : undefined
        }
      />

      <Tarjeta>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="w-64">
            <Selector
              etiqueta={t('existencias.deposito')}
              value={depositoId}
              onChange={(evento) => {
                setDepositoId(evento.target.value);
              }}
            >
              <option value="">{t('existencias.todos')}</option>
              {(depositos.data ?? []).map((deposito) => (
                <option key={deposito.id} value={deposito.id}>
                  {deposito.nombre}
                </option>
              ))}
            </Selector>
          </div>
          {aviso === null ? null : (
            <p role="status" className="text-xs text-bien">
              {aviso}
            </p>
          )}
        </div>

        {consulta.isPending ? <Cargando /> : null}
        {consulta.isError ? (
          <Problema
            error={consulta.error}
            reintentar={() => {
              void consulta.refetch();
            }}
          />
        ) : null}
        {consulta.isSuccess ? (
          <Tabla
            columnas={columnas}
            filas={consulta.data}
            claveDeFila={(fila) => `${fila.depositoId}-${fila.varianteId}`}
            vacio={<Vacio descripcion={t('existencias.vacio')} />}
          />
        ) : null}
      </Tarjeta>

      <Dialogo
        abierto={abierto}
        titulo={t('existencias.ajustar')}
        onCerrar={() => {
          setAbierto(false);
          setError(null);
        }}
        acciones={
          <Boton
            tono="principal"
            cargando={aplicar.isPending}
            disabled={lineas.length === 0}
            onClick={() => {
              aplicar.mutate();
            }}
          >
            {t('existencias.aplicar')}
          </Boton>
        }
      >
        <div className="flex flex-col gap-3">
          {/* El motivo primero y sin valor por defecto útil: es lo que va a explicar el
              movimiento cuando nadie se acuerde de haberlo hecho. */}
          <Selector
            etiqueta={t('existencias.motivo')}
            obligatorio
            value={motivo}
            ayuda={t('existencias.ayudaMotivo')}
            onChange={(evento) => {
              const elegido = MOTIVOS.find((valor) => valor === evento.target.value);
              setMotivo(elegido ?? 'existencia_inicial');
            }}
          >
            {MOTIVOS.map((valor) => (
              <option key={valor} value={valor}>
                {t(claveTraducida('motivo', valor))}
              </option>
            ))}
          </Selector>

          <Campo
            etiqueta={t('existencias.buscarArticulo')}
            type="search"
            value={busquedaDeArticulo}
            onChange={(evento) => {
              setBusquedaDeArticulo(evento.target.value);
            }}
          />

          {(candidatos.data ?? []).length === 0 ? null : (
            <ul className="flex flex-col gap-1">
              {(candidatos.data ?? []).slice(0, 6).map((variante) => (
                <li key={variante.varianteId}>
                  <Boton
                    tono="secundario"
                    onClick={() => {
                      setLineas((previas) => [
                        ...previas,
                        {
                          clave: `${variante.varianteId}-${String(previas.length)}`,
                          varianteId: variante.varianteId,
                          etiqueta: etiquetaDeVariante(variante),
                          cantidad: '',
                          costoUnitario: '',
                          vencimiento: '',
                        },
                      ]);
                      setBusquedaDeArticulo('');
                    }}
                  >
                    {etiquetaDeVariante(variante)}
                  </Boton>
                </li>
              ))}
            </ul>
          )}

          {lineas.map((linea, indice) => (
            <div key={linea.clave} className="rounded-marco border border-borde p-2">
              <p className="mb-2 text-sm font-medium">{linea.etiqueta}</p>
              <div className="grid grid-cols-3 gap-2">
                <Campo
                  etiqueta={t('existencias.cantidad')}
                  obligatorio
                  inputMode="decimal"
                  value={linea.cantidad}
                  ayuda={t('existencias.ayudaCantidad')}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setLineas((previas) =>
                      previas.map((otra, otroIndice) =>
                        otroIndice === indice ? { ...otra, cantidad: valor } : otra,
                      ),
                    );
                  }}
                />
                <Campo
                  etiqueta={t('existencias.costoUnitario')}
                  inputMode="decimal"
                  value={linea.costoUnitario}
                  ayuda={t('existencias.ayudaCosto')}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setLineas((previas) =>
                      previas.map((otra, otroIndice) =>
                        otroIndice === indice ? { ...otra, costoUnitario: valor } : otra,
                      ),
                    );
                  }}
                />
                <Campo
                  etiqueta={t('existencias.vencimiento')}
                  type="date"
                  value={linea.vencimiento}
                  ayuda={t('existencias.ayudaVencimiento')}
                  onChange={(evento) => {
                    const valor = evento.target.value;
                    setLineas((previas) =>
                      previas.map((otra, otroIndice) =>
                        otroIndice === indice ? { ...otra, vencimiento: valor } : otra,
                      ),
                    );
                  }}
                />
              </div>
              <div className="mt-2">
                <Boton
                  tono="sutil"
                  onClick={() => {
                    setLineas((previas) =>
                      previas.filter((_, otroIndice) => otroIndice !== indice),
                    );
                  }}
                >
                  {t('existencias.quitar')}
                </Boton>
              </div>
            </div>
          ))}

          {error === null ? null : (
            <p role="alert" className="text-xs text-mal">
              {error}
            </p>
          )}
        </div>
      </Dialogo>

      {/* La ficha junta las cuatro respuestas que un comercio necesita, y son cuatro porque
          son cuatro preguntas distintas: cuánto me costó la última vez, cuánto vale lo que
          tengo, cuánto saldría reponerlo y cuánto gané de verdad. */}
      <Dialogo
        abierto={costoDe !== null}
        titulo={t('compras.costoYMargen')}
        descripcion={costoDe?.nombre}
        onCerrar={() => {
          setCostoDe(null);
        }}
      >
        {costoDe === null ? null : <CostoYMargen varianteId={costoDe.varianteId} />}
      </Dialogo>

      <Dialogo
        abierto={kardexDe !== null}
        titulo={t('existencias.kardexDe', { articulo: kardexDe?.nombre ?? '' })}
        onCerrar={() => {
          setKardexDe(null);
        }}
      >
        {kardex.isPending ? <Cargando /> : null}
        {kardex.isSuccess ? (
          <Tabla
            columnas={[
              {
                clave: 'cuando',
                titulo: t('existencias.cuando'),
                contenido: (fila: Movimiento) => fila.ocurridoEn.slice(0, 10),
              },
              {
                clave: 'origen',
                titulo: t('existencias.origen'),
                contenido: (fila: Movimiento) => (
                  <>
                    {t(claveTraducida('origen', fila.origen))}
                    {fila.motivo === null ? null : (
                      <span className="block text-xs text-texto-tenue">
                        {t(claveTraducida('motivo', fila.motivo))}
                      </span>
                    )}
                  </>
                ),
              },
              {
                clave: 'cantidad',
                titulo: t('existencias.cantidad'),
                numerica: true,
                contenido: (fila: Movimiento) => (
                  <span
                    className={`numero ${fila.cantidad.startsWith('-') ? 'text-mal' : 'text-bien'}`}
                  >
                    {conDecimales(fila.cantidad, kardexDe?.decimales ?? 0)}
                  </span>
                ),
              },
              {
                clave: 'quien',
                titulo: t('existencias.quien'),
                contenido: (fila: Movimiento) => fila.correoDelUsuario ?? '—',
              },
            ]}
            filas={kardex.data}
            claveDeFila={(fila) => fila.id}
            vacio={<Vacio descripcion={t('existencias.sinMovimientos')} />}
          />
        ) : null}
      </Dialogo>
    </>
  );
}
